import type { CompanySettings, Lead } from '../../../types';
import { formatLeistungszeitraumDisplay } from './offerLeistungszeitraum';
import { contactDisplayName } from '../../../types';
import { formatGermanNumber } from '../../../utils/parseGermanNumber';

export type RecipientLayout = {
  nameLines: string[];
  streetLine: string;
  plzOrtLine: string;
};

/** Breite der Summenspalte (Bruttobetrag + Lohnkosten-Hinweis) in PDF-Punkt — Editor und PDF identisch. */
export const OFFER_TOTALS_COLUMN_WIDTH_PT = 220;

/**
 * Titelsummen-Zusammenstellung aligns with the position table:
 * inset = Pos (7%) + Menge (11%) + Einheit (10%) so labels start like inline „Summe …“;
 * amount column matches Gesamt (14%).
 */
export const OFFER_TITLE_TOTALS_LABEL_INSET_PCT = 28;
export const OFFER_TITLE_TOTALS_AMOUNT_PCT = 14;

export function isOrganizationLikeLead(lead: Lead): boolean {
  const orgName = (lead.organization?.name ?? '').trim();
  if (orgName) return true;
  const c = lead.contact;
  if (!c) return false;
  if ((c.company ?? '').trim()) return true;
  if (c.contact_type === 'Gewerblicher Kunde') return true;
  if (c.contact_kind === 'Gewerblicher Kontakt') return true;
  return false;
}

export function isPrivateCustomerLead(lead: Lead): boolean {
  return !isOrganizationLikeLead(lead);
}

function resolveOrganizationName(lead: Lead): string {
  const orgName = (lead.organization?.name ?? '').trim();
  if (orgName) return orgName;
  const billingCompany = (lead.organization?.billing_contact?.company ?? '').trim();
  if (billingCompany) return billingCompany;
  return (lead.contact?.company ?? '').trim();
}

export function formatLeadPartyName(lead: Lead): string {
  const organizationName = resolveOrganizationName(lead);
  if (organizationName) return organizationName;
  const person = contactDisplayName(lead.contact ?? { name: lead.name }).trim();
  if (person) return person;
  return (lead.name ?? '').trim();
}

export function splitAddressBlock(raw: string): { street: string; plzOrt: string } {
  const t = (raw ?? '').trim();
  if (!t) return { street: '', plzOrt: '' };
  const lines = t
    .split(/\r?\n/)
    .map((s) => s.trim())
    .filter(Boolean);
  if (lines.length >= 2) {
    return { street: lines[0] ?? '', plzOrt: lines.slice(1).join(', ') };
  }
  const one = lines[0] ?? t;
  const m = one.match(/^(.+?)[,;]\s*(\d{4,5}\s+.+)$/);
  if (m) return { street: m[1].trim(), plzOrt: m[2].trim() };
  return { street: one, plzOrt: '' };
}

/** Empfänger für Angebotskopf: Firma/Name getrennt, Adresse in zwei Zeilen. Keine Zeile "Ansprechperson". */
export function formatRecipientForOffer(lead: Lead): RecipientLayout {
  // Rechnungskontakt der Organisation hat Vorrang
  const bc = lead.organization?.billing_contact;
  if (bc) {
    const companyName = resolveOrganizationName(lead);
    const display = contactDisplayName(bc);
    const nameLines: string[] = companyName ? [companyName] : [];
    if (nameLines.length === 0 && display) nameLines.push(display);
    if (nameLines.length === 0 && (bc.name ?? '').trim()) nameLines.push((bc.name ?? '').trim());
    const addr = splitAddressBlock(bc.address ?? '');
    if (nameLines.length > 0 || addr.street) {
      return { nameLines, streetLine: addr.street, plzOrtLine: addr.plzOrt };
    }
  }

  const c = lead.contact;
  if (c) {
    const company = resolveOrganizationName(lead);
    const display = contactDisplayName(c);
    const nameLines: string[] = [];
    if (company) nameLines.push(company);
    if (!isOrganizationLikeLead(lead) && display && (!company || display.toLowerCase() !== company.toLowerCase())) {
      nameLines.push(display);
    }
    if (nameLines.length === 0 && (c.name ?? '').trim()) nameLines.push((c.name ?? '').trim());
    const addr = splitAddressBlock(c.address ?? '');
    if (nameLines.length === 0 && !addr.street && !addr.plzOrt) {
      return formatRecipientFallback(lead);
    }
    return {
      nameLines,
      streetLine: addr.street,
      plzOrtLine: addr.plzOrt,
    };
  }
  return formatRecipientFallback(lead);
}

function formatRecipientFallback(lead: Lead): RecipientLayout {
  const name = (lead.name ?? '').trim();
  const addr = splitAddressBlock(lead.address ?? '');
  const nameLines = name ? [name] : [];
  return { nameLines, streetLine: addr.street, plzOrtLine: addr.plzOrt };
}

export function formatCompanyAddressLines(company: CompanySettings): string[] {
  const street = [company.street?.trim(), company.house_number?.trim()].filter(Boolean).join(' ').trim();
  const line1 = street;
  const plz = (company.postal_code ?? '').trim();
  const city = (company.city ?? '').trim();
  const line2 = [plz, city].filter(Boolean).join(' ').trim();
  return [line1, line2].filter(Boolean);
}

export function formatCompanyCompactSenderLine(company: CompanySettings): string {
  const name = (company.company_name ?? '').trim() || 'Firma';
  const parts = [name, ...formatCompanyAddressLines(company)].filter(Boolean);
  return parts.join(' | ');
}

export function formatCompanyWebsiteUrl(website: string | undefined): string {
  const w = (website ?? '').trim();
  if (!w) return '';
  if (/^https?:\/\//i.test(w)) return w;
  return `https://${w}`;
}

/** Header placeholders: raw values only (no Telefon/E-Mail prefixes). */
export function companyContactFieldLines(
  key: 'companyAddress' | 'companyPhone' | 'companyEmail' | 'companyWebsite',
  company: CompanySettings | null,
  extras?: { websiteDisplay?: string },
): string[] {
  if (!company) return [];
  if (key === 'companyAddress') return formatCompanyAddressLines(company);
  if (key === 'companyPhone') {
    const phone = (company.phone ?? '').trim();
    return phone ? [phone] : [];
  }
  if (key === 'companyEmail') {
    const email = (company.email ?? '').trim();
    return email ? [email] : [];
  }
  const website = (extras?.websiteDisplay ?? company.website ?? '').replace(/^https?:\/\//i, '').trim();
  return website ? [website] : [];
}

export { formatLeistungszeitraumDisplay };

/** Leistungsort: Baustelle, sonst gespiegelte site_address. */
export function formatLeistungsort(lead: Lead): string {
  const b = (lead.baustelle?.address ?? '').trim();
  if (b) return b;
  const s = (lead.site_address ?? '').trim();
  if (s) return s;
  return '—';
}

/**
 * Kundennummer for document layout bands: prefer organization (commercial),
 * fall back to the linked contact (private customers have no org).
 */
export function resolveCustomerNumberLabel(lead: Lead): string | null {
  const fromOrg = (lead.organization?.customer_number ?? '').trim();
  if (fromOrg) return fromOrg;
  const fromContact = (lead.contact?.customer_number ?? '').trim();
  if (fromContact) return fromContact;
  return null;
}

const eurFmt = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });

export function formatEuro(n: number): string {
  if (!Number.isFinite(n)) return eurFmt.format(0);
  return eurFmt.format(n);
}

export function formatDecimal(n: number): string {
  return formatGermanNumber(n);
}

/** Position quantity — up to 3 decimal places (matches lead_positions.quantity). */
export function formatQuantity(n: number): string {
  return formatGermanNumber(n, { minimumFractionDigits: 0, maximumFractionDigits: 3 });
}

export {
  formatPaymentTermsSentence,
  type PaymentTermsSentenceParams,
} from '../../../utils/paymentTerms';

export function formatDirectorFooterLines(company: CompanySettings): string[] {
  const role = (company.director_role ?? '').trim();
  const names = (company.responsible_persons ?? [])
    .map((p) => (p.name ?? '').trim())
    .filter(Boolean);
  if (!role && names.length === 0) return [];
  if (role && names.length > 0) return [`${role}: ${names.join(', ')}`];
  if (role) return [role];
  return names;
}

export function formatRegistryLine(company: CompanySettings): string | null {
  const num = (company.commercial_register_number ?? '').trim();
  const court = (company.registry_court ?? '').trim();
  if (num && court) return `${num} \u00b7 ${court}`;
  if (num) return num;
  if (court) return court;
  return null;
}
